'use client';

import { useMemo, type ComponentType } from 'react';
import type { ListingCard } from '@/src/lib/commerce/wire';
import { localePath, type LocaleRoute } from '@/src/lib/locale';
import type { Messages } from '@/src/lib/messages';
import { useVatDisplay } from './VatDisplay';
import type { ProductGridLayout, ProductGridProps } from './ProductGrid';

/**
 * The seam a vertical's own listing grid is rendered through.
 *
 * <p><strong>Why a wrapper exists at all.</strong> The two listing routes are server
 * components; a vertical's grid is a client component in another package. Two things a grid
 * is handed cannot cross that boundary as the server holds them:
 *
 * <ul>
 *   <li><strong>`hrefFor`.</strong> It is a function, and React refuses to serialise one
 *       into a client component ("Functions cannot be passed directly to Client
 *       Components"). The default grid is a server component, so the routes hand it theirs
 *       and nothing crosses. A client grid would throw on render. What crosses here instead
 *       is the DATA the function is made of — the active locale and the tenant's locale
 *       routing table, both plain — and the same pure {@link localePath} rebuilds the same
 *       function on the client. Identical input, identical output, so a link is the same
 *       string in the server HTML and after hydration.</li>
 *   <li><strong>The VAT basis.</strong> It lives in a browser store (`VatDisplay.tsx`) that
 *       only a hook can read, and a hook cannot be passed to a server-rendered component
 *       either. It is read HERE, in the browser, and handed down as a value — which is also
 *       the only shape that works for a vertical card deriving a second figure from the
 *       price, because that derivation must divide the very figure printed beside it.</li>
 * </ul>
 *
 * <p>What does cross from the server is `Grid` itself: a REFERENCE to a client component,
 * never a call. That is the same shape `catalogueSurface` and `headerChrome` already rely
 * on.
 *
 * <p>The server render — and the first client render, which must match it — shows the public
 * view: inc VAT, no switch. `useVatDisplay`'s own server snapshot guarantees that, so the
 * markup a vertical's card produces on the server is the markup React hydrates.
 *
 * <p>Only the vertical path goes through here. A shop with no vertical grid is rendered by
 * the server component {@link ProductGrid} directly, unchanged — see {@link ListingGrid}.
 */
export function VerticalProductGrid({
  grid: Grid,
  items,
  locale,
  routes,
  messages,
  layout,
}: {
  /** The vertical's grid, as `catalogueComponents.productGrid` declared it. */
  grid: ComponentType<ProductGridProps>;
  items: ListingCard[];
  locale: string;
  /** The tenant's locale routing table, from which `hrefFor` is rebuilt here. */
  routes: LocaleRoute[];
  messages: Messages;
  layout?: ProductGridLayout;
}) {
  const { trade, basis } = useVatDisplay();
  const hrefFor = useMemo(
    () => (publicPath: string) => localePath(publicPath, locale, routes),
    [locale, routes],
  );

  return (
    <Grid
      items={items}
      locale={locale}
      messages={messages}
      hrefFor={hrefFor}
      layout={layout}
      vat={{ trade, basis }}
    />
  );
}
